import {
    type AssistantContentPart,
    type ChatMessage as ChatMessageType,
    isVisibleAssistantContent,
} from '@fruitful/llm-service-shared';
import { WidgetType } from '@fruitful/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { YStack } from 'smoothie';

import type { AssistantContentPartRenderedEvent } from '../../hooks/useAiChatRenderAnalytics';
import { isContentAlreadyTyped } from '../../hooks/useTypewriter';
import { AssistantLogoIconFooter } from './AssistantLogoIconFooter';
import { AssistantMessage } from './AssistantMessage';
import {
    ChatWidgetErrorBoundary,
    isSproutChatWidget,
} from './ChatWidgetErrorBoundary';
import {
    type ChatWidgetHandlers,
    ChatWidgetRenderer,
} from './ChatWidgetRenderer';

interface LogoIconTarget {
    messageId: string;
    contentIndex: number;
}

const SEQUENCING_SAFETY_NET_MIN_MS = 500;
const SEQUENCING_SAFETY_NET_MS_PER_CHAR = 70;

interface SequentialAssistantContentProps {
    message: ChatMessageType;
    contentParts: AssistantContentPart[];
    logoIconTarget: LogoIconTarget | null;
    isStreaming: boolean;
    widgetHandlers: ChatWidgetHandlers;
    /** Show full assistant content immediately (e.g. restored thread history). */
    skipTypingAnimation?: boolean;
    /** Hide inline/footer logo while the turn-level typing indicator is active. */
    suppressLogoIcon?: boolean;
    onContentPartRendered?: (event: AssistantContentPartRenderedEvent) => void;
    /** Fires once all content parts are visible and the last text part finishes typing. */
    onAllPartsRevealed?: () => void;
}

const getTextValue = (part: AssistantContentPart): string => {
    if (part.type !== 'text') return '';
    return typeof part.text === 'string' ? part.text : String(part.text ?? '');
};

const getInitialVisibleCount = (
    parts: AssistantContentPart[],
    skipTypingAnimation: boolean,
): number => {
    if (skipTypingAnimation) {
        return parts.length;
    }

    let count = 0;
    for (const part of parts) {
        if (part.type === 'text') {
            if (isContentAlreadyTyped(getTextValue(part))) {
                count++;
            } else {
                break;
            }
        } else {
            count++;
        }
    }
    return Math.max(1, count);
};

export const SequentialAssistantContent = ({
    message,
    contentParts,
    logoIconTarget,
    isStreaming,
    widgetHandlers,
    skipTypingAnimation = false,
    suppressLogoIcon = false,
    onContentPartRendered,
    onAllPartsRevealed,
}: SequentialAssistantContentProps) => {
    const isMessageStreaming = message.isStreaming ?? false;

    const [visibleCount, setVisibleCount] = useState(() =>
        getInitialVisibleCount(contentParts, skipTypingAnimation),
    );

    useEffect(() => {
        if (skipTypingAnimation) {
            setVisibleCount(contentParts.length);
        }
    }, [contentParts.length, skipTypingAnimation]);

    // contentParts isn't strictly append-only: a widget can be withheld until leading
    // copy exists and then appear, and reorder logic can merge/replace parts at a given
    // index (see reorderAssistantContentParts). So contentParts.length can shrink or
    // hold steady for a turn even as more data arrives. Never persist a downward clamp
    // of visibleCount here — that can strand it below the real content forever once
    // nothing is left mounted to advance it. Bound only at render time so it recovers
    // as soon as content reappears.
    const displayCount = Math.min(visibleCount, contentParts.length);

    useEffect(() => {
        if (skipTypingAnimation) return undefined;
        if (displayCount >= contentParts.length) return undefined;

        const lastRevealed = contentParts[displayCount - 1];
        if (
            lastRevealed?.type === 'ui' &&
            lastRevealed.widget !== WidgetType.InfoCards
        ) {
            const timer = setTimeout(() => {
                setVisibleCount((c) => Math.min(c + 1, contentParts.length));
            }, 0);
            return () => {
                clearTimeout(timer);
            };
        }

        return undefined;
    }, [displayCount, contentParts, skipTypingAnimation]);

    // Safety net: if sequencing ever stalls on an edge case in the reorder logic above,
    // don't leave the turn stuck with content withheld once it's done streaming.
    useEffect(() => {
        if (skipTypingAnimation || isMessageStreaming) return undefined;
        if (displayCount >= contentParts.length) return undefined;

        const stillTyping = contentParts[displayCount - 1];
        const stillTypingLength =
            stillTyping?.type === 'text' ? getTextValue(stillTyping).length : 0;
        const safetyNetMs = Math.max(
            SEQUENCING_SAFETY_NET_MIN_MS,
            stillTypingLength * SEQUENCING_SAFETY_NET_MS_PER_CHAR,
        );

        const timer = setTimeout(() => {
            setVisibleCount(contentParts.length);
        }, safetyNetMs);
        return () => {
            clearTimeout(timer);
        };
    }, [isMessageStreaming, skipTypingAnimation, displayCount, contentParts]);

    const handleTypingComplete = useCallback(() => {
        setVisibleCount((c) => c + 1);
    }, []);

    const onAllPartsRevealedRef = useRef(onAllPartsRevealed);
    onAllPartsRevealedRef.current = onAllPartsRevealed;

    const handleFinalPartTyped = useCallback(() => {
        onAllPartsRevealedRef.current?.();
    }, []);

    const lastPartIsUi =
        contentParts.length > 0 &&
        contentParts[contentParts.length - 1]?.type === 'ui';

    useEffect(() => {
        if (
            lastPartIsUi &&
            displayCount >= contentParts.length &&
            onAllPartsRevealedRef.current != null
        ) {
            onAllPartsRevealedRef.current();
        }
    }, [lastPartIsUi, displayCount, contentParts.length]);

    const visibleParts = contentParts.slice(0, displayCount);
    const isTurnIdle = !isMessageStreaming && !isStreaming && !suppressLogoIcon;

    useEffect(() => {
        if (onContentPartRendered == null) {
            return undefined;
        }

        for (let index = 0; index < displayCount; index += 1) {
            const content = contentParts[index];
            if (content == null) {
                continue;
            }

            if (content.type === 'text') {
                const textValue = getTextValue(content).trim();
                if (textValue !== '') {
                    onContentPartRendered({
                        messageId: message.id,
                        messagePartIndex: index,
                        messageType: content.type,
                        messageLength: textValue.length,
                    });
                }
            } else if (content.type === 'ui') {
                if (isVisibleAssistantContent(message, index)) {
                    onContentPartRendered({
                        messageId: message.id,
                        messagePartIndex: index,
                        messageType: content.type,
                        messageWidgetType: content.widget,
                        messageWidgetProps: content.props,
                    });
                }
            }
        }
    }, [contentParts, displayCount, message, onContentPartRendered]);

    return (
        <>
            {visibleParts.map((content, index) => {
                const contentKey = `${message.id}-${content.type}-${index.toString()}`;
                const isLastVisible = index === displayCount - 1;
                const showFooterLogo =
                    !suppressLogoIcon &&
                    logoIconTarget?.messageId === message.id &&
                    (logoIconTarget.contentIndex === index ||
                        (isTurnIdle &&
                            isLastVisible &&
                            logoIconTarget.contentIndex > index));
                const shouldSequence =
                    !skipTypingAnimation &&
                    isLastVisible &&
                    displayCount < contentParts.length;

                if (content.type === 'text') {
                    const textValue = getTextValue(content);
                    if (textValue.trim() === '') {
                        return null;
                    }

                    const isFinalPart =
                        !shouldSequence && index === contentParts.length - 1;

                    return (
                        <AssistantMessage
                            key={contentKey}
                            content={textValue}
                            isError={message.isError ?? false}
                            showLogoIcon={showFooterLogo}
                            skipTypingAnimation={skipTypingAnimation}
                            isStreaming={isMessageStreaming}
                            onTypingComplete={
                                shouldSequence
                                    ? handleTypingComplete
                                    : isFinalPart
                                      ? handleFinalPartTyped
                                      : undefined
                            }
                        />
                    );
                }

                if (content.type === 'ui') {
                    const isVisible = isVisibleAssistantContent(message, index);

                    if (!isVisible) {
                        return showFooterLogo ? (
                            <AssistantLogoIconFooter key={contentKey} />
                        ) : null;
                    }

                    const widgetRenderer = (
                        <ChatWidgetRenderer
                            messageId={message.id}
                            widget={content.widget}
                            props={content.props}
                            isStreaming={isStreaming}
                            handlers={widgetHandlers}
                            onEntranceComplete={
                                shouldSequence
                                    ? handleTypingComplete
                                    : undefined
                            }
                        />
                    );

                    return (
                        <YStack
                            key={contentKey}
                            width="100%"
                            gap="$3"
                            overflow="visible"
                        >
                            {isSproutChatWidget(content.widget) ? (
                                <ChatWidgetErrorBoundary
                                    widget={content.widget}
                                    messageId={message.id}
                                >
                                    {widgetRenderer}
                                </ChatWidgetErrorBoundary>
                            ) : (
                                widgetRenderer
                            )}
                            {showFooterLogo ? (
                                <AssistantLogoIconFooter />
                            ) : null}
                        </YStack>
                    );
                }

                return null;
            })}
        </>
    );
};
